/**
 * Authenticated fetch for Shopify embedded apps.
 *
 * App Bridge CDN (loaded via script tag) exposes window.shopify.
 * We use idToken() to get a session token for the Authorization header.
 *
 * The fetch URL is made absolute using the app's origin to prevent
 * relative URLs from resolving against admin.shopify.com in the iframe.
 */

declare global {
  interface Window {
    shopify?: {
      idToken: () => Promise<string>;
      config: {
        shop: string;
        apiKey: string;
      };
    };
  }
}

// The app's own origin for absolute URLs
const APP_ORIGIN =
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "";

export function useAuthenticatedFetch() {
  return async (url: string, options: RequestInit = {}) => {
    // Make URL absolute if it starts with /
    const absoluteUrl = url.startsWith("/") ? `${APP_ORIGIN}${url}` : url;

    try {
      const token = await window.shopify?.idToken();

      const headers = new Headers(options.headers);
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      } else {
        console.warn("[AuthFetch] No session token available from App Bridge");
      }

      const response = await fetch(absoluteUrl, {
        ...options,
        headers,
      });

      if (!response.ok) {
        console.error(
          `[AuthFetch] ${options.method || "GET"} ${url} → ${response.status}`
        );
      }

      return response;
    } catch (error) {
      console.error(`[AuthFetch] Failed: ${url}`, error);
      throw error;
    }
  };
}
